import React, { useEffect, useState } from 'react';
import { Activity, IndianRupee, Users, Calendar, Trophy, Building2 } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from 'recharts';
import { api } from '@/lib/api';
import { KPI } from '@/components/ui/Card';
import { formatINR } from '@/lib/utils';
import { useBranding, hexToRgbTriplet } from '@/lib/branding';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  // Fix (branding audit): these charts used a hardcoded "#CCFF00" instead
  // of the platform's configured accent color — recharts renders these as
  // plain SVG fill/stroke attributes, which is its own component prop
  // rather than a Tailwind class, so it needs the actual color value here
  // rather than the CSS-variable trick used elsewhere in the app.
  const { primary_color } = useBranding();
  const accentRgb = hexToRgbTriplet(primary_color) || '204 255 0';
  const accentCursor = `rgba(${accentRgb.replace(/ /g, ',')},0.05)`;

  useEffect(() => {
    api.get('/admin/dashboard/stats').then((r) => setStats(r.data));
  }, []);

  if (!stats) return <div className="text-white/40 text-sm">Loading dashboard…</div>;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Operations Console</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tight">Dashboard</h1>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Total Revenue" value={formatINR(stats.total_revenue)} icon={IndianRupee} tone="volt" />
        <KPI label="Bookings Today" value={stats.bookings_today} icon={Activity} />
        <KPI label="Total Bookings" value={stats.total_bookings} icon={Calendar} />
        <KPI label="Customers" value={stats.total_customers} icon={Users} />
        <KPI label="Active Members" value={stats.active_memberships} icon={Trophy} />
        <KPI label="Active Courts" value={stats.total_courts} icon={Building2} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-surface-raised border border-white/10 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Last 14 days</div>
              <h3 className="font-heading font-bold text-xl uppercase">Revenue trend</h3>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.revenue_series}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="rgba(255,255,255,0.4)" fontSize={10} tickFormatter={(d) => d?.slice(5)} />
                <YAxis stroke="rgba(255,255,255,0.4)" fontSize={10} />
                <Tooltip
                  contentStyle={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 0 }}
                  labelStyle={{ color: '#fff', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em' }}
                  formatter={(v) => formatINR(v)}
                />
                <Line type="monotone" dataKey="revenue" stroke={primary_color} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-surface-raised border border-white/10 p-6">
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Court utilisation</div>
            <h3 className="font-heading font-bold text-xl uppercase">Bookings by court</h3>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.bookings_by_court} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" stroke="rgba(255,255,255,0.4)" fontSize={10} />
                <YAxis dataKey="court" type="category" stroke="rgba(255,255,255,0.6)" fontSize={10} width={110} tickFormatter={(v) => v?.replace('Court ', '')} />
                <Tooltip
                  contentStyle={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 0 }}
                  cursor={{ fill: accentCursor }}
                />
                <Bar dataKey="count" fill={primary_color} radius={0} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
